<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper/types'
import 'swiper/css'

const props = defineProps<{
  block: BlockImages,
  noHorizontalPadding?: boolean,
}>()

// hasMany uploads are typed as (string | Media)[] because Payload can return
// unpopulated ids — the GraphQL fragment always selects the full object.
const images = computed(() => (props.block.images ?? []).filter(image => typeof image !== 'string') as Media[])

// One shape for the whole block: every image is shown through it.
const maskShape = computed(() => (props.block.maskShape && typeof props.block.maskShape !== 'string' ? props.block.maskShape : null))

// The mask shape brings its own box, so the fixed landscape stage below only
// applies to plain images.
const isMasked = computed(() => Boolean(props.block.useMask && maskShape.value))

// The shape masks the carousel, not the pictures in it: one box, sitting still
// while the images move underneath it, instead of a shape travelling with each
// slide. That box is also what gives the block its height when it is masked.
const maskStyle = computed(() => {
  const shape = maskShape.value
  if (!isMasked.value || !shape?.url) return undefined
  return useMaskStyle({ ...shape, url: shape.url })
})

const activeIndex = ref(0)
const swiperInstance = ref<SwiperType>()

// The block wraps rather than stopping: past the last image is the first one.
// A single image has no wrap to make, and no ends to stop at either.
const loop = computed(() => images.value.length > 1)

const onSwiperInit = (swiper: SwiperType) => {
  swiperInstance.value = swiper
}

// Looping duplicates slides, so Swiper's own index counts those too —
// `realIndex` is the one that points back into `images`.
const onSlideChange = (swiper: SwiperType) => {
  activeIndex.value = swiper.realIndex
}

watch(images, () => {
  activeIndex.value = 0
  swiperInstance.value?.slideTo(0, 0)
})

const activeImage = computed(() => images.value[activeIndex.value])

const caption = computed(() => (props.block.hideCaptions
  ? null
  : captionWithCredit(activeImage.value?.caption, activeImage.value?.credit)))

// Under a mask the caption can start where the shape's bottom edge starts
// rather than at the block's left edge. The shape's box is the full block
// width and maps 1:1 onto its viewBox (see useMaskStyle), so the fraction
// parseSvgBottomLeft finds is the same fraction of the block width — and a
// percentage margin resolves against exactly that.
const captionStyle = computed(() => {
  if (!props.block.alignCaptionToMask || !isMasked.value || !maskShape.value?.svgContent) return undefined
  const offset = parseSvgBottomLeft(maskShape.value.svgContent)
  return offset ? { marginLeft: `${offset * 100}%` } : undefined
})

// Every slide is shown in the same box, so stepping through the block never
// moves the arrows or reflows the page. Landscape fills it edge to edge;
// anything taller is fitted inside instead of cropped.
// Asked per image rather than of the active one: every slide is drawn at once.
const fitClass = (image: Media) => (image.width && image.height
  && getOrientation(image.width, image.height) === 'landscape'
  ? 'object-cover'
  : 'object-contain')

const prev = () => swiperInstance.value?.slidePrev()
const next = () => swiperInstance.value?.slideNext()

// Air kept on both sides of an arrow that sits outside the block: between the
// block and the arrow, and between the arrow and the edge of the viewport.
const ARROW_GAP = 20

const stage = useTemplateRef<HTMLElement>('stage')
const arrow = useTemplateRef<HTMLElement>('arrow')
const { left, right } = useElementBounding(stage)
const { width: arrowWidth } = useElementSize(arrow)
const { width: viewportWidth } = useWindowSize()

// How far outside the block an arrow is pushed, measured from the arrow itself.
const arrowOffset = computed(() => arrowWidth.value + ARROW_GAP)

// Arrows sit in the margin beside the block for as long as both margins can
// take one with air left over on the outside; once they can't, they move on top
// of the image. Before the arrow has been measured (SSR, first paint) its width
// reads as 0, so the inside placement is what renders first — it always fits.
const arrowsOutside = computed(() => {
  if (!arrowWidth.value) return false
  const needed = arrowOffset.value + ARROW_GAP
  return left.value >= needed && viewportWidth.value - right.value >= needed
})
</script>

<template>
  <section class="container-d my-3 select-none" :class="{ 'px-1': !noHorizontalPadding }">
    <!-- `isolate` keeps the arrows' z-index local to the block. Without it they
         resolve against the root stacking context, where the site header's
         pinned search/menu buttons also sit at z-1 — same layer, later in the
         document, so an arrow scrolled up to the top of the viewport paints
         over those buttons and takes their clicks. -->
    <div ref="stage" class="relative isolate" :class="{ 'aspect-[16/11]': !isMasked }">
      <!-- The mask lives on this box rather than on the slides, so the shape
           holds its place while the carousel runs through the images behind
           it. Unmasked, the box is the stage itself. -->
      <div
        :class="isMasked ? 'relative' : 'absolute inset-0'"
        :style="maskStyle"
      >
        <!-- Every image of the block is a slide, so all of them are in the DOM
             from the start: lazysizes loads them alongside the one on screen,
             and stepping slides a picture that is already there instead of
             mounting a fresh <img> and waiting on it. -->
        <Swiper
          class="block-images-swiper"
          :modules="[A11y]"
          :loop="loop"
          @swiper="onSwiperInit"
          @slide-change="onSlideChange"
        >
          <SwiperSlide
            v-for="image in images"
            :key="image.id"
            class="block-images-slide"
          >
            <!-- Filling the box in both directions is what lets object-fit
                 decide how a non-landscape image sits in it, so the image's own
                 aspect ratio stays out of the way. -->
            <core-image
              :image="image"
              :apply-aspect-ratio-style="false"
              class="absolute inset-0 w-full h-full"
              :class="fitClass(image)"
            />
          </SwiperSlide>
        </Swiper>
      </div>

      <button
        v-if="images.length > 1"
        ref="arrow"
        type="button"
        :aria-label="$t('images.previous')"
        class="group z-1 absolute top-1/2 -translate-y-1/2 icon-box-arrow flex items-center justify-start"
        :style="{ left: arrowsOutside ? `${-arrowOffset}px` : 'var(--unit--vertical)' }"
        @click="prev"
      >
        <svgo-arrow class="overflow-visible icon-arrow stroke-current stroke-default fill-none text-accent group-hover:text-black icon-hover" />
      </button>

      <button
        v-if="images.length > 1"
        type="button"
        :aria-label="$t('images.next')"
        class="group z-1 absolute top-1/2 -translate-y-1/2 icon-box-arrow flex items-center justify-end"
        :style="{ right: arrowsOutside ? `${-arrowOffset}px` : 'var(--unit--vertical)' }"
        @click="next"
      >
        <!-- The half-turn goes through the standalone `rotate` property, not through
             `transform`: `icon-hover` transitions transform, and a rotation living in
             there gets animated along with the hover scale. -->
        <svgo-arrow class="overflow-visible icon-arrow [rotate:180deg] stroke-current stroke-default fill-none text-accent group-hover:text-black icon-hover" />
      </button>
    </div>

    <core-rich-text
      v-if="caption"
      :content="caption"
      class="mt-0_35"
      :style="captionStyle"
      text-size-class="text-xs"
    />
  </section>
</template>

<style scoped>
/* swiper.css centres .swiper with `margin-inline: auto`; the box around it is
   the thing doing the centring here, so the carousel just takes the whole of
   it. */
.block-images-swiper {
  width: 100%;
  height: 100%;
  margin-inline: 0;
}

/* The slide is an empty box until the image inside it is positioned against
   something. */
.block-images-slide {
  position: relative;
}
</style>
